import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { ErrorList, type ListOfErrors } from '#app/components/forms'
import { cn } from '#app/utils/misc'
import { consentSegments } from '#app/utils/site/auth-ui'
import { siteContent } from '#app/utils/site/content'
import { SITE_LINKS } from '#app/utils/site/site-links'

const UNDERLINE =
	'font-semibold text-tt-ink underline underline-offset-[3px] rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tt-ink'

/** The paragraph under a heading ("We'll send a verification code..."). */
export function AuthSub({
	children,
	className,
}: {
	children: ReactNode
	className?: string
}) {
	return (
		<p
			className={cn(
				'tt-auth-sub mb-[18px] mt-2 text-[15px] leading-[1.45] text-tt-body min-[601px]:mb-[30px] min-[601px]:mt-[18px] min-[601px]:text-[18px]',
				className,
			)}
		>
			{children}
		</p>
	)
}

/** The bold email inside AuthSub; wraps anywhere so long addresses fit. */
export function AuthStrong({ children }: { children: ReactNode }) {
	return (
		<b className="font-semibold text-tt-ink [overflow-wrap:anywhere]">
			{children}
		</b>
	)
}

/** "Already have an account? Log in": a centred line with one link. */
export function AuthAlt({
	text,
	link,
	className,
}: {
	text: string
	link: { label: string; to: string }
	className?: string
}) {
	return (
		<p
			className={cn(
				'tt-auth-alt my-3 text-center text-[14px] font-medium min-[601px]:my-[clamp(10px,1.8vh,18px)] min-[601px]:text-[15px]',
				className,
			)}
		>
			{text}{' '}
			<Link to={link.to} className={UNDERLINE}>
				{link.label}
			</Link>
		</p>
	)
}

/** "Or continue with" between two hairlines, under the main form's button. */
export function AuthDivider({ className }: { className?: string }) {
	return (
		<div
			className={cn(
				'tt-auth-or mx-6 mt-5 mb-3 flex items-center gap-4 text-[14px] text-tt-muted before:h-px before:flex-1 before:bg-tt-line after:h-px after:flex-1 after:bg-tt-line min-[601px]:mx-7 min-[601px]:mt-[clamp(14px,2.4vh,24px)] min-[601px]:mb-[clamp(10px,1.8vh,18px)]',
				className,
			)}
			aria-hidden="true"
		>
			<span>{siteContent.auth.or}</span>
		</div>
	)
}

/**
 * The consent sentence under the buttons, with Terms of Service and
 * Privacy Policy linked (the login terms checkbox is gone: WEBSITE-V2.md).
 */
export function AuthConsent({
	kind,
	className,
}: {
	kind: 'signup' | 'login'
	className?: string
}) {
	const sentence = siteContent.auth[kind].consent
	const labels = siteContent.legal.switcher
	const segments = consentSegments(sentence, labels)
	const hrefs = { terms: SITE_LINKS.terms, privacy: SITE_LINKS.privacy }
	return (
		<p
			className={cn(
				'mt-3.5 text-left text-[13px] leading-[1.5] text-tt-muted min-[601px]:mt-[clamp(12px,2vh,20px)]',
				className,
			)}
		>
			{segments.map((segment, index) =>
				segment.kind === 'link' ? (
					<Link
						key={`${segment.to}-${index}`}
						to={hrefs[segment.to]}
						className="rounded-sm font-medium text-tt-ink underline underline-offset-[3px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tt-ink"
					>
						{segment.text}
					</Link>
				) : (
					<span key={`text-${index}`}>{segment.text}</span>
				),
			)}
		</p>
	)
}

/**
 * The round back control above a heading. A link when it has a known
 * destination; a button (the route passes `navigate(-1)`) when the previous
 * screen varies, as on /verify.
 */
export function AuthBackButton({
	to,
	onClick,
	label = siteContent.auth.fields.back,
	className,
}: {
	to?: string
	onClick?: () => void
	label?: string
	className?: string
}) {
	const classes = cn(
		'tt-auth-back mb-3.5 inline-flex h-10 w-10 items-center justify-center rounded-full border-[1.5px] border-tt-ink text-tt-ink transition-colors duration-200 hover:bg-tt-ink hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tt-ink focus-visible:ring-offset-2 min-[521px]:h-[46px] min-[521px]:w-[46px] min-[601px]:mb-[clamp(18px,3vh,30px)]',
		className,
	)
	const arrow = (
		<svg
			viewBox="0 0 24 24"
			width="20"
			height="20"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M19 12H5M11 18l-6-6 6-6" />
		</svg>
	)
	if (to) {
		return (
			<Link to={to} aria-label={label} className={classes}>
				{arrow}
			</Link>
		)
	}
	return (
		<button
			type="button"
			onClick={onClick}
			aria-label={label}
			className={classes}
		>
			{arrow}
		</button>
	)
}

/** Form-level errors (conform's `form.errors`), in the reference's red 14px. */
export function AuthErrors({
	errors,
	id,
	className,
}: {
	errors?: ListOfErrors
	id?: string
	className?: string
}) {
	const hasErrors = Boolean(errors?.filter(Boolean).length)
	if (!hasErrors) return null
	return (
		<div
			className={cn('mb-3.5 mt-1 pl-[18px] min-[601px]:pl-[22px]', className)}
		>
			<ErrorList id={id} errors={errors} className="tt-auth-errors" />
		</div>
	)
}

/** "Forgot Password?" under the password field on the login screens. */
export function AuthForgotLink({
	to,
	className,
}: {
	to: string
	className?: string
}) {
	return (
		<Link
			to={to}
			className={cn(
				'mb-3.5 inline-block rounded-sm text-[13.5px] font-semibold text-tt-ink hover:underline hover:underline-offset-[3px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tt-ink min-[601px]:mb-[clamp(10px,2.2vh,22px)] min-[601px]:mt-0.5 min-[601px]:text-[14px]',
				className,
			)}
		>
			{siteContent.auth.login.forgot}
		</Link>
	)
}
